import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { debounce, formatMoney, startViewTransition } from '@theme/utilities';
import { autoApplyInventoryLevelFilter, clearAllFiltersExceptInventory } from './inventory-level-filter.js';
import { LoadManager } from './custom-collection-utility.js';
import { updateAllProductCardsWithQuantities } from './product-card-updater.js';


/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 * 
 * @example
 * <!-- To disable URL updates for filters, add the disable-url-updates attribute -->
 * <facets-form-component 
 *   class="facets__form-wrapper" 
 *   section-id="{{ section.id }}"
 *   disable-url-updates
 * >
 * 
 * @example
 * <!-- JavaScript usage -->
 * const facetsForm = document.querySelector('facets-form-component');
 * 
 * // Disable URL updates
 * facetsForm.setURLUpdatesDisabled();
 * 
 * // Enable URL updates
 * facetsForm.enableURLUpdates();
 * 
 * // Check if URL updates are disabled
 * if (facetsForm.isURLUpdatesDisabled()) {
 *   // Use updateFiltersWithoutURL() for no URL updates
 *   await facetsForm.updateFiltersWithoutURL();
 * } else {
 *   // Use updateFilters() for normal behavior with URL updates
 *   await facetsForm.updateFilters();
 * }
 * 
 * // Update filters by URL without changing browser URL
 * await facetsForm.updateFiltersByURLWithoutBrowserUpdate('/collections/all?filter.v.product_type=Food');
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  connectedCallback() {
    try {
      super.connectedCallback();
      
      // Check if URL updates should be disabled
      this.disableURLUpdates = this.hasAttribute('disable-url-updates');
      // this.dataCaptureDeliveryMoment = this.getAttribute('data-capture-delivery-moment') || 'false';
      
      // Log the current setting for debugging
      // console.log('🔍 [FACETS] FacetsFormComponent connected, disableURLUpdates:', this.disableURLUpdates);
      
      this.autoApplyMondayProductionFilter();

      // if(this.dataCaptureDeliveryMoment && this.dataCaptureDeliveryMoment === 'true') {
      //   // Auto-apply Monday production filter immediately when component is connected
      //   this.autoApplyMondayProductionFilter();
      // }
      // else {
      //   /** @type {any} */ (window).InventoryLevelFilter?.applyStepProductTypeFilters?.(this);
      //   this.updateFiltersWithoutURL();
      // }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Whether URL updates are disabled
   * @type {boolean}
   */
  disableURLUpdates = false;

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   * @returns {Promise<void>} Promise that resolves when section rendering is complete
   */
  updateFilters = async () => {
    try {

      // Check if URL updates should be disabled
      if (this.disableURLUpdates) {
        await this.updateFiltersWithoutURL();
        return;
      }

      this.#updateURLHash();
      this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
      await this.#updateSection();
      this.#syncQuickFilters();
    } catch (error) {
      throw error;
    }
  };

  /**
   * Updates filters without changing the browser URL
   * @returns {Promise<void>} Promise that resolves when section rendering is complete
   */
  updateFiltersWithoutURL = async () => {
    // Create a temporary URL with filter parameters for the API call
    const tempUrl = new URL(window.location.pathname, window.location.origin);
    const urlParameters = this.createURLParameters();
    
    // Add filter parameters to temp URL
    for (const [param, value] of urlParameters.entries()) {
      tempUrl.searchParams.append(param, value);
    }
    
    // Use the temp URL for section rendering but don't update browser URL
    await this.#updateSectionWithCustomURL(tempUrl);
    this.#syncQuickFilters();
  };

  /**
   * Enables URL updates for filters
   */
  enableURLUpdates() {
    this.disableURLUpdates = false;
    this.removeAttribute('disable-url-updates');
  }

  /**
   * Disables URL updates for filters
   */
  setURLUpdatesDisabled() {
    this.disableURLUpdates = true;
    this.setAttribute('disable-url-updates', '');
  }

  /**
   * Gets whether URL updates are currently disabled
   * @returns {boolean} True if URL updates are disabled
   */
  isURLUpdatesDisabled() {
    return this.disableURLUpdates;
  }

  /**
   * Gets the current URL update configuration status
   * @returns {Object} Object containing URL update configuration details
   */
  getURLUpdateStatus() {
    return {
      disabled: this.disableURLUpdates,
      hasAttribute: this.hasAttribute('disable-url-updates'),
      attributeValue: this.getAttribute('disable-url-updates'),
      currentUrl: window.location.href
    };
  }

  /**
   * Auto-applies inventory level filter based on delivery day or defaults to Monday production
   */
  async autoApplyMondayProductionFilter() {
    try {

      // Add loading spinner to body ( we might not need this because there is already a loader from @custom-collection-footer )
       LoadManager.addLoader();
      
      // First, check if there are URL parameters that need to be applied and cleaned up
      // const urlParamsApplied = await this.#applyAndCleanupURLParameters();
    
      // Check if URL updates should be disabled before auto-applying filters
      if (this.disableURLUpdates) {
        // console.log('🔍 [FACETS] Auto-applying Monday production filter without URL updates');
        // Use the original autoApplyInventoryLevelFilter function which now respects URL update settings
        await autoApplyInventoryLevelFilter(this);
      } else {
        // console.log('🔍 [FACETS] Auto-applying Monday production filter with URL updates');
        await autoApplyInventoryLevelFilter(this);
      }

      
    } catch (error) {
      // console.error('🔍 [FACETS] Error auto-applying Monday production filter:', error);

      //( we might not need this because there is already a loader from @custom-collection-footer )
      LoadManager.removeLoader();
    }
    finally {
      //( we might not need this because there is already a loader from @custom-collection-footer )
      LoadManager.removeLoader();
    }
  }

  // /**
  //  * Applies URL parameters to the form and cleans up the URL
  //  * @returns {Promise<boolean>} True if URL parameters were applied and cleaned up
  //  */
  // async #applyAndCleanupURLParameters() {
  //   try {
  //     const currentUrl = new URL(window.location.href);
  //     const urlParams = currentUrl.searchParams;
      
  //     // Check if there are any filter parameters in the URL
  //     const hasFilterParams = Array.from(urlParams.keys()).some(key => 
  //       key.startsWith('filter.') || key.startsWith('sort_by') || key === 'q'
  //     );
      
  //     if (!hasFilterParams) {
  //       console.log('🔍 [FACETS] No filter parameters found in URL');
  //       return false;
  //     }
      
  //     console.log('🔍 [FACETS] Found filter parameters in URL:', Array.from(urlParams.entries()));
      
      
  //     // Clean up the URL by removing filter parameters
  //     this.#cleanupURL();
      
  //     console.log('🔍 [FACETS] URL cleaned up');
  //     return true;
      
  //   } catch (error) {
  //     console.error('🔍 [FACETS] Error cleaning up URL parameters:', error);
      
  //     return false;
  //   }
  // }


  /**
   * Cleans up the URL by removing filter parameters
   */
  // #cleanupURL() {
  //   const currentUrl = new URL(window.location.href);
  //   const cleanUrl = new URL(currentUrl.pathname, currentUrl.origin);
    
  //   // Only preserve non-filter parameters (like page, view, etc.)
  //   for (const [key, value] of currentUrl.searchParams.entries()) {
  //     if (!key.startsWith('filter.') && !key.startsWith('sort_by') && key !== 'q') {
  //       cleanUrl.searchParams.append(key, value);
  //     }
  //   }
    
  //   // Update the browser URL without triggering a page reload
  //   history.replaceState({}, '', cleanUrl.toString());
  //   console.log('🔍 [FACETS] URL cleaned up:', cleanUrl.toString());
  // }

  /**
   * Updates the section
   * @returns {Promise<void>} Promise that resolves when section rendering is complete
   */
  async #updateSection() {
    const viewTransition = !this.closest('dialog');

    if (viewTransition) {
      await startViewTransition(() => sectionRenderer.renderSection(this.sectionId), ['product-grid']);
    } else {
      await sectionRenderer.renderSection(this.sectionId);
    }
    await updateAllProductCardsWithQuantities(true);
  }

  /**
   * Updates the section with a custom URL (doesn't change browser URL)
   * @param {URL} customUrl - The custom URL to use for section rendering
   * @returns {Promise<void>} Promise that resolves when section rendering is complete
   */
  async #updateSectionWithCustomURL(customUrl) {
    const viewTransition = !this.closest('dialog');

    if (viewTransition) {
      await startViewTransition(() => sectionRenderer.renderSectionWithCustomURL(this.sectionId, customUrl, { cache: false }), ['product-grid']);
    } else {
      await sectionRenderer.renderSectionWithCustomURL(this.sectionId, customUrl, { cache: false });
    }
    await updateAllProductCardsWithQuantities(true);
  }

  /**
   * Syncs quick filter states by dispatching a custom event
   */
  #syncQuickFilters() {
    // Dispatch a custom event that quick filter components can listen for
    document.dispatchEvent(new CustomEvent('sync-quick-filters'));
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   * @returns {Promise<void>} Promise that resolves when section rendering is complete
   */
  async updateFiltersByURL(url) {
    history.pushState('', '', url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    await this.#updateSection();
    this.#syncQuickFilters();
  }

  /**
   * Updates filters based on a provided URL without changing browser URL
   * @param {string} url - The URL to update filters with
   * @returns {Promise<void>} Promise that resolves when section rendering is complete
   */
  async updateFiltersByURLWithoutBrowserUpdate(url) {
    // Parse the URL to get parameters
    const urlObj = new URL(url, window.location.origin);
    
    // Create a temporary URL for section rendering
    const tempUrl = new URL(window.location.pathname, window.location.origin);
    
    // Copy all search parameters from the provided URL
    for (const [param, value] of urlObj.searchParams.entries()) {
      tempUrl.searchParams.append(param, value);
    }
    
    // Use the temp URL for section rendering but don't update browser URL
    await this.#updateSectionWithCustomURL(tempUrl);
    this.#syncQuickFilters();
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   * @returns {Promise<void>} Promise that resolves when filters are updated
   */
  async updateFilters() {
    try {
      const facetsForm = this.closest('facets-form-component');

      if (!(facetsForm instanceof FacetsFormComponent)) {
        return;
      }

      await facetsForm.updateFilters();
      this.#updateSelectedFacetSummary();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  async handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      await this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   * @returns {Promise<void>} Promise that resolves when filters are updated
   */
  async updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    await facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const value = Number(input.value);
    const min = Number(formatMoney(input.getAttribute('data-min') ?? ''));
    const max = Number(formatMoney(input.getAttribute('data-max') ?? ''));

    if (value < min) input.value = min.toString();
    if (value > max) input.value = max.toString();
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   * @returns {Promise<void>} Promise that resolves when filters are updated
   */
  async clearFilter(event) {
    try {
      if (!(event.target instanceof HTMLElement)) return;

      if (event instanceof KeyboardEvent) {
        if (event.key !== 'Enter' && event.key !== ' ') {
          return;
        }
        event.preventDefault();
      }

      // Add breadcrumb for filter clearing
      

      const container = event.target.closest('facet-inputs-component, price-facet-component');
      container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
        if (input instanceof HTMLInputElement) {
          input.checked = false;
          input.value = '';
        }
      });

      const details = event.target.closest('details');
      const statusComponent = details?.querySelector('facet-status-component');

      if (!(statusComponent instanceof FacetStatusComponent)) return;

      statusComponent.clearSummary();

      const facetsForm = this.closest('facets-form-component');
      if (!(facetsForm instanceof FacetsFormComponent)) return;

      await facetsForm.updateFilters();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = async (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') await this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   * @returns {Promise<void>} Promise that resolves when filters are updated
   */
  async removeFilter({ form }, event) {
    try {
      if (event instanceof KeyboardEvent) {
        if (event.key !== 'Enter' && event.key !== ' ') {
          return;
        }
        event.preventDefault();
      }

      

      const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

      if (!(facetsForm instanceof FacetsFormComponent)) {
        return;
      }

      // Clear all filters except inventory level filters
      const success = await clearAllFiltersExceptInventory(facetsForm);
      
      // Fallback to original behavior if clearing fails
      if (!success) {
        const url = this.dataset.url;
        if (url) {
          // Use the smart method that respects URL update preferences
          if (facetsForm.disableURLUpdates && typeof facetsForm.updateFiltersByURLWithoutBrowserUpdate === 'function') {
            await facetsForm.updateFiltersByURLWithoutBrowserUpdate(url);
          } else {
            await facetsForm.updateFiltersByURL(url);
          }
        }
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = /** @type {string[]} */ ([]); // No required refs since we only use select element now

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return; // Desktop version not available

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return; // Desktop version not available

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   * @returns {Promise<void>} Promise that resolves when filters are updated
   */
  async updateFilterAndSorting(event) {
    try {
      const facetsForm =
        this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

      if (!(facetsForm instanceof FacetsFormComponent)) {
        return;
      }

      

      // Since we only have one select element now, we can directly update filters
      await facetsForm.updateFilters();
      this.updateFacetStatus(event);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};
